import { createTRPCProxyClient, httpBatchLink } from '@trpc/client';
import axios from 'axios';
import superjson from 'superjson';
import { z } from 'zod';

import type { AppRouter } from 'iriko/src/router';

const lensProfileResponse = z.object({
  data: z.object({
    challenge: z.object({
      text: z.string(),
    }),
  }),
});

// Notice the <AppRouter> generic here.
const trpc = createTRPCProxyClient<AppRouter>({
  transformer: superjson,
  links: [
    httpBatchLink({
      url: 'https://sdk-iriko.huddle01.com/trpc',
    }),
  ],
});

export const getMessage = (address: string) =>
  trpc.auth.signMessage.mutate({ address });

export const getLensMessage = async (address: string) => {
  const { data } = await axios.request({
    method: 'POST',
    url: 'https://api.lens.dev/',
    headers: { 'Content-Type': 'application/json' },
    data: {
      query: `query Challenge($request: ChallengeRequest!) {
                challenge(request: $request) { text }
              }`,
      variables: {
        request: {
          address: address,
        },
      },
    },
  });

  const text = lensProfileResponse.parse(data).data.challenge.text;

  return {
    message: text,
  };
};

export const getAccessToken = (signature: string, address: string) =>
  trpc.auth.login.mutate({
    signature,
    chain: 'ethereum',
    walletName: 'metamask',
    address,
  });

export const getSolanaAccessToken = (signature: string, address: string) =>
  trpc.auth.login.mutate({
    phantomSignature: signature,
    chain: 'solana',
    walletName: 'phantom',
    address,
  });

export const getLensAccessToken = async (
  signature: string,
  address: string
) => {
  try {
    const result = async () =>
      await axios.request<{
        data: {
          authenticate: {
            accessToken: string;
          };
        };
      }>({
        method: 'POST',
        url: 'https://api.lens.dev/',
        headers: { 'Content-Type': 'application/json' },
        data: {
          query: `mutation ($request: SignedAuthChallenge!) { 
            authenticate(request: $request) {
              accessToken
              refreshToken
            }
          }`,
          variables: {
            request: { address, signature },
          },
        },
      });

    const lensToken = await result();

    const ld = await trpc.auth.login.mutate({
      address,
      chain: 'lens',
      accessToken: lensToken.data.data.authenticate.accessToken,
      walletName: 'lens',
    });

    return { accessToken: ld.accessToken };
  } catch (error) {
    throw new Error('Error getting lens access token');
  }
};
